import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, Power, PowerOff, UserPlus, UserMinus, Crown,
  Users as UsersIcon, Search, History, ToggleLeft, ToggleRight, Shield,
  UserCheck, ExternalLink,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { FormField } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_IN_TEAM_LABELS = {
  team_lead: "Team Lead",
  physician: "Physician",
  nurse: "Nurse",
  care_coordinator: "Care Coordinator",
  field_team: "Field Team",
  dietitian: "Dietitian",
  social_worker: "Social Worker",
  pharmacist: "Pharmacist",
  respiratory_therapist: "Respiratory Therapist",
  other: "Other",
};

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const WRITE_ROLES = new Set([
  "admin", "clinical_director", "hah_coordinator", "care_coordinator",
]);

function MemberAvatar({ name }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="w-9 h-9 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center font-bold text-sm shrink-0">
      {initial}
    </div>
  );
}

export default function CareTeams() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.has(user?.role_code);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [query, setQuery] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // create | edit
  const [editing, setEditing] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTeam, setDetailTeam] = useState(null);
  const [detailTab, setDetailTab] = useState("members");

  const [confirm, setConfirm] = useState({ open: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (query.trim()) params.q = query.trim();
      const r = await api.get("/care-teams", { params });
      setItems(r.data.data ?? []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query]);

  useEffect(() => { load(); }, [load, statusFilter]);
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [query, load]);

  function openCreate() {
    setEditing({ name: "", description: "", team_lead_id: "", members: [] });
    setEditorMode("create");
    setEditorOpen(true);
  }
  function openEdit(team) {
    setEditing({
      id: team.id,
      name: team.name,
      description: team.description || "",
      team_lead_id: team.team_lead_id || "",
    });
    setEditorMode("edit");
    setEditorOpen(true);
  }

  async function openDetail(teamId) {
    try {
      const r = await api.get(`/care-teams/${teamId}`);
      setDetailTeam(r.data.data);
      setDetailTab("members");
      setDetailOpen(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function refreshDetail() {
    if (!detailTeam?.id) return;
    try {
      const r = await api.get(`/care-teams/${detailTeam.id}`);
      setDetailTeam(r.data.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function setStatus(team, action) {
    try {
      await api.post(`/care-teams/${team.id}/${action}`);
      toast.success(`Team ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function askDelete(team) {
    setConfirm({
      open: true,
      title: `Delete care team`,
      description: `Are you sure you want to remove "${team.name}"? This soft-deletes the team and cannot be undone from the UI. Active onboarding assignments will block deletion.`,
      confirmLabel: "Delete",
      tone: "destructive",
      onConfirm: async () => {
        try {
          await api.delete(`/care-teams/${team.id}`);
          toast.success("Care team deleted");
          load();
        } catch (e) { toast.error(formatApiError(e)); }
      },
    });
  }

  const totals = useMemo(() => {
    const t = { total: items.length, active: 0, inactive: 0, members: 0 };
    items.forEach((it) => {
      if (it.status === "active") t.active += 1;
      if (it.status === "inactive") t.inactive += 1;
      t.members += it.active_member_count || 0;
    });
    return t;
  }, [items]);

  return (
    <div data-testid="care-teams-page">
      <PageHeader
        title="Care Teams"
        subtitle="Build and manage reusable home-care teams — assign a team lead, add clinicians, and toggle availability."
        testId="care-teams-header"
        actions={canWrite ? (
          <Button onClick={openCreate} data-testid="care-teams-create-btn" className="bg-[#0B7E8C] hover:bg-[#0a6e7a]">
            <Plus className="w-4 h-4 mr-1.5" /> New Care Team
          </Button>
        ) : null}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Total teams" value={totals.total} testId="ct-kpi-total" />
        <KPI label="Active" value={totals.active} accent="#10B981" testId="ct-kpi-active" />
        <KPI label="Inactive" value={totals.inactive} accent="#94A3B8" testId="ct-kpi-inactive" />
        <KPI label="Active members" value={totals.members} accent="#0B7E8C" testId="ct-kpi-members" />
      </div>

      {/* Filters */}
      <div className="hah-card p-3 mb-3 flex flex-col md:flex-row gap-2 md:items-center" data-testid="care-teams-filters">
        <div className="relative md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by team name…"
            className="pl-9"
            data-testid="care-teams-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-48" data-testid="care-teams-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cards grid */}
      {loading ? (
        <div className="text-sm text-[#6B7280] p-8 text-center hah-card" data-testid="care-teams-loading">Loading care teams…</div>
      ) : items.length === 0 ? (
        <div className="hah-card p-10 text-center" data-testid="care-teams-empty">
          <UsersIcon className="w-10 h-10 mx-auto text-[#CBD5E1] mb-2" />
          <div className="text-[#1F2937] font-semibold">No care teams yet</div>
          <div className="text-sm text-[#6B7280] mt-1">
            {canWrite
              ? "Click \"New Care Team\" above to create your first team."
              : "Ask the H@H Care Coordinator to create care teams."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="care-teams-grid">
          {items.map((t) => (
            <TeamCard
              key={t.id}
              team={t}
              canWrite={canWrite}
              onOpen={() => openDetail(t.id)}
              onEdit={() => openEdit(t)}
              onActivate={() => setStatus(t, "activate")}
              onDeactivate={() => setStatus(t, "deactivate")}
              onDelete={() => askDelete(t)}
            />
          ))}
        </div>
      )}

      {/* Editor sheet */}
      <TeamEditorSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        mode={editorMode}
        editing={editing}
        onSaved={() => { setEditorOpen(false); load(); }}
      />

      {/* Detail sheet */}
      <TeamDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        team={detailTeam}
        tab={detailTab}
        setTab={setDetailTab}
        canWrite={canWrite}
        onChanged={() => { refreshDetail(); load(); }}
        onEdit={(t) => { setDetailOpen(false); openEdit(t); }}
      />

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        description={confirm.description}
        confirmLabel={confirm.confirmLabel || "Confirm"}
        tone={confirm.tone}
        onCancel={() => setConfirm({ open: false })}
        onConfirm={async () => {
          const fn = confirm.onConfirm;
          setConfirm({ open: false });
          if (fn) await fn();
        }}
      />
    </div>
  );
}

function KPI({ label, value, accent = "#0B7E8C", testId }) {
  return (
    <div className="hah-card p-3" data-testid={testId}>
      <div className="text-xs text-[#6B7280]">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function TeamCard({ team, canWrite, onOpen, onEdit, onActivate, onDeactivate, onDelete }) {
  const lead = team.team_lead;
  return (
    <div
      className="hah-card p-4 hover:shadow-md transition cursor-pointer flex flex-col gap-3"
      onClick={onOpen}
      data-testid={`care-team-card-${team.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-[#1F2937] truncate">{team.name}</div>
          {team.description && (
            <div className="text-xs text-[#6B7280] line-clamp-2 mt-0.5">{team.description}</div>
          )}
        </div>
        <StatusBadge status={team.status} />
      </div>

      <div className="text-xs text-[#475569] flex items-center gap-2">
        <Crown className="w-3.5 h-3.5 text-[#D97706]" />
        <span className="font-medium">Lead:</span>
        <span className="truncate">{lead?.name || <span className="text-[#94A3B8] italic">Not assigned</span>}</span>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-[#0B7E8C]"><UsersIcon className="w-3.5 h-3.5" />{team.active_member_count} active</span>
        <span className="text-[#94A3B8]">/ {team.total_member_count} total</span>
      </div>

      {/* Member preview */}
      <div className="flex -space-x-2">
        {(team.members || []).slice(0, 5).map((m) => (
          <div
            key={m.user_id}
            title={`${m.name} — ${ROLE_IN_TEAM_LABELS[m.role_in_team] || m.role_in_team}`}
            className={`w-7 h-7 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-[11px] font-bold border-2 border-white ${m.active ? "" : "opacity-40 grayscale"}`}
          >
            {(m.name || "?").charAt(0).toUpperCase()}
          </div>
        ))}
        {(team.members || []).length > 5 && (
          <div className="w-7 h-7 rounded-full bg-[#F1F5F9] text-[#475569] flex items-center justify-center text-[10px] font-bold border-2 border-white">
            +{team.members.length - 5}
          </div>
        )}
      </div>

      {/* Action buttons */}
      {canWrite && (
        <div className="flex items-center gap-1 pt-1 border-t border-[#E5E7EB]" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" className="text-xs" onClick={onEdit} data-testid={`care-team-edit-${team.id}`}>
            <Edit className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          {team.status === "active" ? (
            <Button size="sm" variant="ghost" className="text-xs text-[#94A3B8]" onClick={onDeactivate} data-testid={`care-team-deactivate-${team.id}`}>
              <PowerOff className="w-3.5 h-3.5 mr-1" /> Deactivate
            </Button>
          ) : team.status === "inactive" ? (
            <Button size="sm" variant="ghost" className="text-xs text-[#10B981]" onClick={onActivate} data-testid={`care-team-activate-${team.id}`}>
              <Power className="w-3.5 h-3.5 mr-1" /> Activate
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button size="sm" variant="ghost" className="text-xs text-[#EF4444]" onClick={onDelete} data-testid={`care-team-delete-${team.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function TeamEditorSheet({ open, onOpenChange, mode, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", team_lead_id: "" });
  const [members, setMembers] = useState([]); // [{user_id, role_in_team, active}]
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [roleInTeamOptions, setRoleInTeamOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: editing?.name || "",
      description: editing?.description || "",
      team_lead_id: editing?.team_lead_id || "",
    });
    setMembers([]); // editor only handles details + initial members on create
    api.get("/care-teams/lookup/available-members")
      .then((r) => {
        setUsers(r.data.data ?? []);
        setRoleInTeamOptions(r.data.meta?.role_in_team_options ?? []);
      })
      .catch(() => {});
  }, [open, editing]);

  const userById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  function addMember(uid) {
    if (!uid || members.some((m) => m.user_id === uid)) return;
    setMembers((prev) => [...prev, { user_id: uid, role_in_team: "field_team", active: true }]);
  }
  function removeMember(uid) {
    setMembers((prev) => prev.filter((m) => m.user_id !== uid));
  }
  function setMemberRole(uid, role) {
    setMembers((prev) => prev.map((m) => (m.user_id === uid ? { ...m, role_in_team: role } : m)));
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error("Team name is required");
      return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        const payload = {
          name: form.name.trim(),
          description: form.description?.trim() || null,
          team_lead_id: form.team_lead_id || null,
          members,
        };
        await api.post("/care-teams", payload);
        toast.success("Care team created");
      } else {
        const payload = {
          name: form.name.trim(),
          description: form.description?.trim() || null,
          team_lead_id: form.team_lead_id || null,
        };
        await api.put(`/care-teams/${editing.id}`, payload);
        toast.success("Care team updated");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="care-team-editor-sheet">
        <SheetHeader>
          <SheetTitle>{mode === "create" ? "Create Care Team" : "Edit Care Team"}</SheetTitle>
          <SheetDescription>
            {mode === "create"
              ? "Name your team, assign a lead, and optionally add initial members."
              : "Update team name, description, or change the team lead."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <FormField label="Team Name" required>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Riyadh North Team"
              data-testid="care-team-name-input"
            />
          </FormField>
          <FormField label="Description (optional)">
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Coverage area, specialty focus, on-call hours…"
              data-testid="care-team-desc-input"
            />
          </FormField>
          <FormField label="Team Lead">
            <Select
              value={form.team_lead_id || "__none__"}
              onValueChange={(v) => setForm((f) => ({ ...f, team_lead_id: v === "__none__" ? "" : v }))}
            >
              <SelectTrigger data-testid="care-team-lead-select"><SelectValue placeholder="Select lead" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No lead —</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} · {(u.role_code || "").replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>

          {mode === "create" && (
            <div>
              <div className="text-sm font-medium text-[#1F2937] mb-1">Initial Members</div>
              <div className="text-xs text-[#6B7280] mb-2">Members can also be added later from the team detail page.</div>
              <Select
                value="__add__"
                onValueChange={(v) => addMember(v)}
              >
                <SelectTrigger data-testid="care-team-add-member-select"><SelectValue placeholder="Add a member…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__add__">— Select to add —</SelectItem>
                  {users
                    .filter((u) => !members.some((m) => m.user_id === u.id))
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} · {(u.role_code || "").replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {members.length > 0 && (
                <div className="mt-2 border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB]">
                  {members.map((m) => {
                    const u = userById.get(m.user_id);
                    return (
                      <div key={m.user_id} className="p-2 flex items-center gap-2">
                        <MemberAvatar name={`${u?.first_name || ""} ${u?.last_name || ""}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{u?.first_name} {u?.last_name}</div>
                          <div className="text-xs text-[#6B7280] truncate">{u?.email}</div>
                        </div>
                        <Select value={m.role_in_team} onValueChange={(v) => setMemberRole(m.user_id, v)}>
                          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {roleInTeamOptions.map((opt) => (
                              <SelectItem key={opt} value={opt}>{ROLE_IN_TEAM_LABELS[opt] || opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="icon" variant="ghost" onClick={() => removeMember(m.user_id)} title="Remove">
                          <UserMinus className="w-4 h-4 text-[#EF4444]" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="care-team-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#0a6e7a]" data-testid="care-team-save-btn">
            {saving ? "Saving…" : mode === "create" ? "Create Team" : "Save Changes"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TeamDetailSheet({ open, onOpenChange, team, tab, setTab, canWrite, onChanged, onEdit }) {
  const [users, setUsers] = useState([]);
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState("field_team");
  const [roleInTeamOptions, setRoleInTeamOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/care-teams/lookup/available-members")
      .then((r) => {
        setUsers(r.data.data ?? []);
        setRoleInTeamOptions(r.data.meta?.role_in_team_options ?? []);
      })
      .catch(() => {});
  }, [open]);

  if (!team) return null;

  const memberIds = new Set((team.members || []).map((m) => m.user_id));
  const eligibleUsers = users.filter((u) => !memberIds.has(u.id));

  async function addMember() {
    if (!addUserId) return;
    try {
      await api.post(`/care-teams/${team.id}/members`, { user_id: addUserId, role_in_team: addRole, active: true });
      toast.success("Member added");
      setAddUserId("");
      setAddRole("field_team");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function removeMember(uid) {
    try {
      await api.delete(`/care-teams/${team.id}/members/${uid}`);
      toast.success("Member removed");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function toggleMember(m) {
    try {
      await api.patch(`/care-teams/${team.id}/members/${m.user_id}`, { active: !m.active });
      toast.success(m.active ? "Member deactivated" : "Member activated");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function changeMemberRole(m, role) {
    try {
      await api.patch(`/care-teams/${team.id}/members/${m.user_id}`, { role_in_team: role });
      toast.success("Role updated");
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[720px] overflow-y-auto" data-testid="care-team-detail-sheet">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <SheetTitle className="flex items-center gap-2">
                <span className="truncate">{team.name}</span>
                <StatusBadge status={team.status} />
              </SheetTitle>
              <SheetDescription>
                {team.description || "No description."}
              </SheetDescription>
            </div>
            {canWrite && (
              <Button size="sm" variant="outline" onClick={() => onEdit(team)} data-testid="care-team-detail-edit">
                <Edit className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="mt-3 text-xs text-[#475569] grid grid-cols-2 gap-2">
          <div><span className="text-[#94A3B8]">Lead:</span> {team.team_lead?.name || <em>None</em>}</div>
          <div><span className="text-[#94A3B8]">Members:</span> {team.active_member_count}/{team.total_member_count}</div>
          <div><span className="text-[#94A3B8]">Created:</span> {(team.created_at || "").slice(0, 10)} · {team.created_by_name}</div>
          <div><span className="text-[#94A3B8]">Updated:</span> {(team.updated_at || "").slice(0, 10)} · {team.updated_by_name}</div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList>
            <TabsTrigger value="members" data-testid="care-team-tab-members"><UsersIcon className="w-3.5 h-3.5 mr-1" />Members</TabsTrigger>
            <TabsTrigger value="patients" data-testid="care-team-tab-patients"><UserCheck className="w-3.5 h-3.5 mr-1" />Patients</TabsTrigger>
            <TabsTrigger value="audit" data-testid="care-team-tab-audit"><History className="w-3.5 h-3.5 mr-1" />Audit Trail</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="mt-3 space-y-3">
            {canWrite && (
              <div className="hah-card p-3 flex flex-col md:flex-row gap-2 md:items-end" data-testid="care-team-add-member-row">
                <div className="flex-1">
                  <div className="text-xs text-[#6B7280] mb-1">Add member</div>
                  <Select value={addUserId || "__pick__"} onValueChange={(v) => setAddUserId(v === "__pick__" ? "" : v)}>
                    <SelectTrigger data-testid="care-team-add-user-select"><SelectValue placeholder="Choose user" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__pick__">— Pick a user —</SelectItem>
                      {eligibleUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} · {(u.role_code || "").replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:w-52">
                  <div className="text-xs text-[#6B7280] mb-1">Role in team</div>
                  <Select value={addRole} onValueChange={setAddRole}>
                    <SelectTrigger data-testid="care-team-add-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {roleInTeamOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{ROLE_IN_TEAM_LABELS[opt] || opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={addMember} disabled={!addUserId} className="bg-[#0B7E8C] hover:bg-[#0a6e7a]" data-testid="care-team-add-member-confirm">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Add
                </Button>
              </div>
            )}

            <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="care-team-members-list">
              {(team.members || []).length === 0 ? (
                <div className="p-6 text-sm text-[#6B7280] text-center">No members yet.</div>
              ) : team.members.map((m) => (
                <div key={m.user_id} className={`p-3 flex items-center gap-3 ${m.active ? "" : "opacity-60"}`} data-testid={`care-team-member-${m.user_id}`}>
                  <MemberAvatar name={m.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F2937] flex items-center gap-2">
                      <span className="truncate">{m.name}</span>
                      {team.team_lead_id === m.user_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                          <Crown className="w-3 h-3" /> Lead
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#6B7280] truncate">
                      {(m.role_code || "").replace(/_/g, " ")} · {m.email}
                    </div>
                  </div>
                  {canWrite ? (
                    <Select value={m.role_in_team} onValueChange={(v) => changeMemberRole(m, v)}>
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid={`care-team-member-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleInTeamOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>{ROLE_IN_TEAM_LABELS[opt] || opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs text-[#475569]">{ROLE_IN_TEAM_LABELS[m.role_in_team] || m.role_in_team}</span>
                  )}
                  {canWrite && (
                    <>
                      <Button size="icon" variant="ghost" title={m.active ? "Deactivate" : "Activate"} onClick={() => toggleMember(m)} data-testid={`care-team-member-toggle-${m.user_id}`}>
                        {m.active
                          ? <ToggleRight className="w-5 h-5 text-[#10B981]" />
                          : <ToggleLeft className="w-5 h-5 text-[#94A3B8]" />}
                      </Button>
                      <Button size="icon" variant="ghost" title="Remove" onClick={() => removeMember(m.user_id)} data-testid={`care-team-member-remove-${m.user_id}`}>
                        <UserMinus className="w-4 h-4 text-[#EF4444]" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="patients" className="mt-3">
            <CareTeamPatientsTab team={team} canWrite={canWrite} onChanged={onChanged} />
          </TabsContent>

          <TabsContent value="audit" className="mt-3">
            <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="care-team-audit-list">
              {(team.audit || []).length === 0 ? (
                <div className="p-6 text-sm text-[#6B7280] text-center">No audit entries yet.</div>
              ) : team.audit.map((a) => (
                <div key={a.id} className="p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-[#1F2937]">{a.message}</div>
                    <div className="text-xs text-[#94A3B8]">{(a.created_at || "").replace("T", " ").slice(0, 19)}</div>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5 flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    <span className="capitalize">{(a.action || "").replace(/_/g, " ")}</span> · {a.actor_name}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function CareTeamPatientsTab({ team, canWrite, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/care-teams/${team.id}/patients`);
      setItems(r.data.data ?? []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [team.id]);

  useEffect(() => { load(); }, [load]);

  // Patient search for the assign picker
  useEffect(() => {
    if (!pickerOpen) return;
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/patients", { params: { q: query, per_page: 25 } })
        .then((r) => {
          const list = r.data.data ?? [];
          // Exclude already assigned ones
          const already = new Set(items.map((p) => p.id));
          setSearchResults(list.filter((p) => !already.has(p.id)));
        })
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query, pickerOpen, items]);

  async function assign(patientId) {
    try {
      await api.post(`/care-teams/${team.id}/patients`, { patient_id: patientId });
      toast.success("Patient assigned");
      setQuery("");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function unassign(patientId) {
    try {
      await api.delete(`/care-teams/${team.id}/patients/${patientId}`);
      toast.success("Patient unassigned");
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-3" data-testid="care-team-patients-tab">
      {canWrite && (
        <div className="hah-card p-3" data-testid="care-team-assign-patient-row">
          {!pickerOpen ? (
            <Button
              onClick={() => setPickerOpen(true)}
              className="bg-[#0B7E8C] hover:bg-[#0a6e7a]"
              data-testid="care-team-open-assign-patient"
            >
              <UserPlus className="w-4 h-4 mr-1.5" /> Assign Patient
            </Button>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-[#94A3B8]" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or MRN…"
                  className="h-9"
                  data-testid="care-team-patient-search"
                />
                <Button size="sm" variant="ghost" onClick={() => { setPickerOpen(false); setQuery(""); }}>
                  Cancel
                </Button>
              </div>
              <div className="border border-[#E5E7EB] rounded-lg max-h-64 overflow-y-auto divide-y divide-[#E5E7EB]" data-testid="care-team-patient-results">
                {searching ? (
                  <div className="p-3 text-xs text-[#6B7280]">Searching…</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3 text-xs text-[#6B7280]">No matching patients.</div>
                ) : searchResults.slice(0, 10).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => assign(p.id)}
                    className="w-full text-left p-2 hover:bg-[#F1F5F9] flex items-center gap-2"
                    data-testid={`care-team-patient-pick-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-sm font-bold">
                      {(p.first_name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.first_name} {p.last_name}</div>
                      <div className="text-xs text-[#6B7280] truncate">{p.mrn} · <span className="capitalize">{p.status}</span></div>
                    </div>
                    <UserPlus className="w-4 h-4 text-[#0B7E8C]" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="hah-card divide-y divide-[#E5E7EB]" data-testid="care-team-patients-list">
        {loading ? (
          <div className="p-6 text-sm text-[#6B7280] text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center">
            <UserCheck className="w-8 h-8 mx-auto text-[#CBD5E1] mb-1.5" />
            <div className="text-sm text-[#6B7280]">No patients assigned to this team yet.</div>
            {canWrite && <div className="text-xs text-[#94A3B8] mt-1">Use &ldquo;Assign Patient&rdquo; above to get started.</div>}
          </div>
        ) : items.map((p) => (
          <div key={p.id} className="p-3 flex items-center gap-3" data-testid={`care-team-patient-${p.id}`}>
            <div className="w-9 h-9 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center font-bold text-sm">
              {(p.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#1F2937] truncate">{p.name}</div>
              <div className="text-xs text-[#6B7280] truncate">
                <span className="font-mono">{p.mrn}</span>
                {p.status && <> · <span className="capitalize">{p.status}</span></>}
                {p.city && <> · {p.city}</>}
              </div>
            </div>
            <a href={`/clinical/patients/${p.id}`} target="_blank" rel="noopener noreferrer" title="Open patient">
              <Button size="icon" variant="ghost" data-testid={`care-team-patient-open-${p.id}`}>
                <ExternalLink className="w-4 h-4 text-[#475569]" />
              </Button>
            </a>
            {canWrite && (
              <Button size="icon" variant="ghost" title="Unassign" onClick={() => unassign(p.id)} data-testid={`care-team-patient-unassign-${p.id}`}>
                <UserMinus className="w-4 h-4 text-[#EF4444]" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
